import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../store";
import {
  customPaginationPageSize,
  customPaginationPageSizeSelector,
  ADMIN_PAGE_NO_ACCESS,
  SEARCH_TIMER,
  EMOJI_ERROR_MESSAGE,
} from "../../Constant";
import {
  MotifTable,
  MotifButton,
  MotifIcon,
  MotifToast,
  MotifErrorMessage,
} from "@ey-xd/motif-react";
import { IconoirPlus } from "@ey-xd/motif-icon";
import NoAccess from "../../components/NoAccess";
import CustomPagination from "../../components/CustomPagination";
import {
  deleteAdminSupportUser,
  fetchAdminSupportListData,
  resetAddUserError,
  resetDeleteUserError,
  SystemRoles,
  updateAdminSupportUserStatus,
} from "../../store/slices/AdminSupportSlice";
import {
  getAdminSupportTableColumns,
  PaginationType,
  AdminSupportRow,
} from "./AdminSupportColumns.tsx";
import AdminSupportModal from "../../components/AdminSupportModal";
import ErrorOrderPage from "../../components/ErrorOrderPage/ErrorOrderPage";
import NoSearchResult from "../../components/NoSearchResult/NoSearchResult";
import errorImage from "../../assets/images/empty_product_list_default.svg";
import "./AdminSupport.scss";
import ConfirmationModal from "../../components/ConfirmationModal/ConfirmationModal.tsx";
import SearchInput from "../../components/SearchInput/SearchInput.tsx";
import { containsEmoji } from "../../utils/common.ts";

export type AdminSupportToast = {
  variant: "info" | "success" | "error";
  message: string;
};

const AdminSupport = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const userPermissionsData: string[] = useSelector(
    (state: RootState) => state.UserProfileSlice.userPermissions,
  );
  const emailId: string = useSelector(
    (state: RootState) => state.UserProfileSlice.emailId,
  );
  const isLoading = useSelector(
    (state: RootState) => state.AdminSupportSlice.isLoading,
  );
  const apiData = useSelector(
    (state: RootState) => state.AdminSupportSlice.data,
  );
  const error = useSelector(
    (state: RootState) => state.AdminSupportSlice.error,
  );

  const isDeletingUser = useSelector(
    (state: RootState) => state.AdminSupportSlice.isDeletingUser,
  );

  const deleteUserError = useSelector(
    (state: RootState) => state.AdminSupportSlice.deleteUserError,
  );

  const [showAdminSupportModal, setShowAdminSupportModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [adminSupportToEdit, setAdminSupportToEdit] =
    useState<AdminSupportRow | null>(null);
  const [toastMessage, setToastMessage] = useState<AdminSupportToast | null>(
    null,
  );
  const [paginationObj, setPaginationObj] = useState<PaginationType>({
    currentPage: 1,
    pageSize: customPaginationPageSize,
    searchText: "",
    SortOrder: "ASC",
    SortColumn: "Name",
  });
  const [deleteConfirmationModalVisible, setDeleteConfirmationModalVisible] =
    useState(false);
  const [selectedRowForDeletion, setSelectedRowForDeletion] =
    useState<AdminSupportRow | null>(null);
  const [toggleConfirmationModalVisible, setToggleConfirmationModalVisible] =
    useState(false);
  const [selectedRowForToggle, setSelectedRowForToggle] =
    useState<AdminSupportRow | null>(null);
  const [isTogglingActive, setIsTogglingActive] = useState(false);
  const adminSupportPageRef = useRef<HTMLDivElement>(null);
  const [searchValidationError, setSearchValidationError] =
    useState<string>("");
  const [searchValue, setSearchValue] = useState<string>("");

  //temporary check to mitigate the horizontal scroll issue
  const setDeleteConfirmationVisibility = useCallback((isVisible: boolean) => {
    const page = adminSupportPageRef.current;

    if (isVisible) {
      const scrollbarWidth =
        window.innerWidth - document.documentElement.clientWidth;

      if (page && scrollbarWidth > 0) {
        page.style.paddingRight = `${scrollbarWidth}px`;
      }
      document.body.style.overflow = "hidden";
    } else if (page) {
      page.style.paddingRight = "";
    }

    setDeleteConfirmationModalVisible(isVisible);
  }, []);

  const setToggleConfirmationVisibility = useCallback((isVisible: boolean) => {
    const page = adminSupportPageRef.current;

    if (isVisible) {
      const scrollbarWidth =
        window.innerWidth - document.documentElement.clientWidth;

      if (page && scrollbarWidth > 0) {
        page.style.paddingRight = `${scrollbarWidth}px`;
      }
      document.body.style.overflow = "hidden";
    } else if (page) {
      page.style.paddingRight = "";
    }

    setToggleConfirmationModalVisible(isVisible);
  }, []);

  useEffect(() => {
    if (toastMessage) {
      const timer = setTimeout(() => {
        setToastMessage(null);
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [toastMessage]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [apiData]);

  const hasAdminAccess = useMemo(() => {
    if (!userPermissionsData?.length) return false;
    return userPermissionsData.some(
      (permission) => permission?.toLowerCase() === "system_admin",
    );
  }, [userPermissionsData]);

  useEffect(() => {
    if (!hasAdminAccess) {
      return;
    }
    dispatch(
      fetchAdminSupportListData({
        page: paginationObj.currentPage,
        record: paginationObj.pageSize,
        search: paginationObj.searchText,
        sortOrder: paginationObj.SortOrder,
        sortColumn: paginationObj.SortColumn,
      }),
    );
  }, [dispatch, hasAdminAccess, paginationObj]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (containsEmoji(searchValue)) {
        return;
      }

      setPaginationObj((prev) => {
        if (prev.searchText === searchValue && prev.currentPage === 1) {
          return prev;
        }

        return {
          ...prev,
          currentPage: 1,
          searchText: searchValue,
        };
      });
    }, SEARCH_TIMER);

    return () => clearTimeout(timer);
  }, [searchValue]);

  const handleSearch = (searchTerm: string) => {
    setSearchValue(searchTerm);
  };

  const handleClearSearch = () => {
    setSearchValidationError("");
    setSearchValue("");
  };

  const handleRefresh = useCallback(() => {
    dispatch(
      fetchAdminSupportListData({
        page: paginationObj.currentPage,
        record: paginationObj.pageSize,
        search: paginationObj.searchText,
        sortOrder: paginationObj.SortOrder,
        sortColumn: paginationObj.SortColumn,
      }),
    );
  }, [
    dispatch,
    paginationObj.SortColumn,
    paginationObj.SortOrder,
    paginationObj.currentPage,
    paginationObj.pageSize,
    paginationObj.searchText,
  ]);

  const handleSortChange = useCallback((columnName: string) => {
    setPaginationObj((prev: any) => {
      // New column or default state starts at ascending.
      if (prev.SortColumn !== columnName || prev.SortOrder === "") {
        return {
          ...prev,
          SortColumn: columnName,
          SortOrder: "ASC",
        };
      }

      if (prev.SortOrder === "ASC") {
        return {
          ...prev,
          SortColumn: columnName,
          SortOrder: "DESC",
        };
      }

      return {
        ...prev,
        SortColumn: "",
        SortOrder: "",
      };
    });
  }, []);

  const handleCloseAdminSupportModal = useCallback(
    async (type: "discard" | "add" | "edit" | "cancel") => {
      document.body.style.overflow = "unset";
      setShowAdminSupportModal(false);
      setIsEdit(false);
      setAdminSupportToEdit(null);
      switch (type) {
        case "discard":
          setToastMessage({
            variant: "info",
            message: "Changes discarded",
          });
          break;
        case "add":
          setSearchValue("");
          setSearchValidationError("");
          setPaginationObj((prev) => ({
            ...prev,
            currentPage: 1,
            searchText: "",
            SortOrder: "ASC",
            SortColumn: "Name",
          }));
          setToastMessage({
            variant: "success",
            message: "User added",
          });
          break;
        case "edit":
          await dispatch(
            fetchAdminSupportListData({
              page: paginationObj.currentPage,
              record: paginationObj.pageSize,
              search: paginationObj.searchText,
              sortOrder: paginationObj.SortOrder,
              sortColumn: paginationObj.SortColumn,
            }),
          );
          setToastMessage({
            variant: "success",
            message: "Changes saved successfully",
          });
          break;
        case "cancel":
          setToastMessage(null);
      }
    },
    [
      dispatch,
      paginationObj.SortColumn,
      paginationObj.SortOrder,
      paginationObj.currentPage,
      paginationObj.pageSize,
      paginationObj.searchText,
    ],
  );

  const handleEdit = useCallback(
    (row: AdminSupportRow) => {
      dispatch(resetAddUserError());
      setAdminSupportToEdit(row);
      setIsEdit(true);
      setShowAdminSupportModal(true);
    },
    [dispatch],
  );

  const onCancelModalAction = () => {
    setDeleteConfirmationVisibility(false);
    setSelectedRowForDeletion(null);
    if (deleteUserError) dispatch(resetDeleteUserError());
  };

  const handleDelete = async () => {
    if (!selectedRowForDeletion) return;
    if (isDeletingUser) return;
    const userId = selectedRowForDeletion.UserId as number;
    await dispatch(
      deleteAdminSupportUser({
        Action: "Delete",
        UserId: userId,
      }),
    ).unwrap();
    setDeleteConfirmationVisibility(false);
    setSelectedRowForDeletion(null);
    if (!apiData) return;
    const totalRecordsAfterDelete = apiData.TotalRecord - 1;
    const lastPage = Math.ceil(
      totalRecordsAfterDelete / paginationObj.pageSize,
    );
    if (paginationObj.currentPage > lastPage) {
      setPaginationObj((prev) => ({
        ...prev,
        pageSize: Number(paginationObj.pageSize),
        currentPage: lastPage,
      }));
    } else {
      await dispatch(
        fetchAdminSupportListData({
          page: paginationObj.currentPage,
          record: paginationObj.pageSize,
          search: paginationObj.searchText,
          sortOrder: paginationObj.SortOrder,
          sortColumn: paginationObj.SortColumn,
        }),
      ).unwrap();
    }
    setToastMessage({
      variant: "success",
      message: "User deleted",
    });
  };

  const handleToggleActive = useCallback(
    (row: AdminSupportRow) => {
      setSelectedRowForToggle(row);
      setToggleConfirmationVisibility(true);
    },
    [setToggleConfirmationVisibility],
  );

  const onCancelToggleAction = useCallback(() => {
    setToggleConfirmationVisibility(false);
    setSelectedRowForToggle(null);
  }, [setToggleConfirmationVisibility]);

  const confirmToggleActive = useCallback(async () => {
    if (!selectedRowForToggle) return;
    setIsTogglingActive(true);
    try {
      const response = await dispatch(
        updateAdminSupportUserStatus({
          Action: "updateStatus",
          Email: String(selectedRowForToggle.Email || ""),
          IsActive: !selectedRowForToggle.IsActive,
          Role: String(selectedRowForToggle.RoleCd || "") as SystemRoles,
        }),
      );
      const payload = response.payload as {
        message?: string;
        Message?: string;
        Status?: string;
      };
      const errorMsg = payload?.message || payload?.Message || "";
      if (
        updateAdminSupportUserStatus.rejected.match(response) ||
        errorMsg.includes("Bad Request:")
      ) {
        setToastMessage({
          variant: "error",
          message: errorMsg,
        });
        window.scrollTo({
          top: 0,
          behavior: "smooth",
        });
      } else {
        setToastMessage({
          variant: "success",
          message: `User ${selectedRowForToggle.IsActive ? "deactivated" : "activated"} .`,
        });
      }
    } catch {
      setToastMessage({
        variant: "error",
        message:
          "An error occurred while updating the user role. Please try again.",
      });
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    } finally {
      setIsTogglingActive(false);
      setToggleConfirmationVisibility(false);
      setSelectedRowForToggle(null);
    }
  }, [dispatch, selectedRowForToggle, setToggleConfirmationVisibility]);

  const tableColumns = useMemo(
    () =>
      getAdminSupportTableColumns(
        handleSortChange,
        paginationObj,
        handleEdit,
        setDeleteConfirmationVisibility,
        setSelectedRowForDeletion,
        emailId,
        handleToggleActive,
      ),
    [
      handleSortChange,
      paginationObj,
      handleEdit,
      setDeleteConfirmationVisibility,
      emailId,
      handleToggleActive,
    ],
  );

  const rowData = useMemo<AdminSupportRow[]>(
    () => (apiData?.Result as AdminSupportRow[]) || [],
    [apiData],
  );
  const totalRecords =
    apiData && typeof apiData.TotalRecord === "number"
      ? apiData.TotalRecord
      : 0;
  const totalPages =
    apiData && typeof apiData.TotalPages === "number" ? apiData.TotalPages : 0;

  if (!hasAdminAccess) {
    return (
      <NoAccess
        headerText={ADMIN_PAGE_NO_ACCESS.HEADER}
        descriptionText={ADMIN_PAGE_NO_ACCESS.DESCRIPTION}
        descriptionText2={ADMIN_PAGE_NO_ACCESS.SUGGESTION}
        buttonLabel={ADMIN_PAGE_NO_ACCESS.BUTTON_LABEL}
        onClickHandler={() => navigate("/")}
      />
    );
  }

  return (
    <div ref={adminSupportPageRef} className="admin-support-page">
      <div className="admin-support-container">
        <div className="admin-support-table-container">
          <div className="admin-support-toast">
            {toastMessage && (
              <MotifToast
                variant={toastMessage.variant}
                onClose={() => setToastMessage(null)}
              >
                <div slot="toast-supporting-text">{toastMessage.message}</div>
              </MotifToast>
            )}
          </div>
          <MotifTable
            className="admin-section-table"
            domLayout="autoHeight"
            columnTypes={{}}
            columnDefs={tableColumns}
            loading={isLoading}
            noRowsOverlayComponent={
              error
                ? () => (
                    <div className="admin-support-no-rows-overlay">
                      <ErrorOrderPage
                        image={errorImage}
                        header="An error has occurred while attempting to retrieve data. Please choose the option to refresh the page."
                        handleRefresh={handleRefresh}
                      />
                    </div>
                  )
                : searchValue
                  ? () => (
                      <div className="admin-support-no-rows-overlay">
                        <NoSearchResult searchValue={searchValue} />
                      </div>
                    )
                  : ""
            }
            suppressMovableColumns
            defaultColDef={{
              resizable: false,
            }}
            suppressCellFocus
            rowData={rowData}
            title="Admin & support"
            titleComplement={
              <div className="admin-support-actions">
                <div className="admin-support-search-container">
                  <SearchInput
                    id={"admin-search-details"}
                    aria-label={"Search"}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      const isContainEmoji = containsEmoji(e.target.value);
                      setSearchValidationError(
                        isContainEmoji ? EMOJI_ERROR_MESSAGE : "",
                      );
                      handleSearch(e.target.value);
                    }}
                    clearSearch={handleClearSearch}
                    onEnter={(e: React.KeyboardEvent<HTMLInputElement>) => {
                      const isContainEmoji = containsEmoji(
                        e.currentTarget.value,
                      );
                      if (!isContainEmoji) {
                        handleSearch(e.currentTarget.value);
                      }
                    }}
                    placeholder={"Search"}
                    value={searchValue}
                    withOutDebounce
                    className="admin-support-search-input"
                  />
                  {searchValidationError && (
                    <MotifErrorMessage className="search-helper-text">
                      {searchValidationError}
                    </MotifErrorMessage>
                  )}
                </div>
                <MotifButton
                  size="medium"
                  variant="primary-alt"
                  onClick={() => {
                    dispatch(resetAddUserError());
                    setIsEdit(false);
                    setAdminSupportToEdit(null);
                    setShowAdminSupportModal(true);
                  }}
                >
                  Add user
                  <MotifIcon iconFunction={IconoirPlus} size="20" />
                </MotifButton>
              </div>
            }
          />
          {totalRecords > 0 && (
            <div className="pagination-container">
              <CustomPagination
                currentpage={paginationObj.currentPage}
                pageSize={paginationObj.pageSize}
                disableNext={false}
                onChangePageSize={(page: number) => {
                  setPaginationObj((prev) => ({
                    ...prev,
                    pageSize: Number(page),
                    currentPage: 1,
                  }));
                }}
                onChangeNextPage={() => {
                  setPaginationObj((prev) => ({
                    ...prev,
                    currentPage: prev.currentPage + 1,
                  }));
                }}
                onChangeFirstPage={() => {
                  setPaginationObj((prev) => ({
                    ...prev,
                    currentPage: 1,
                  }));
                }}
                onChangePreviousPage={() => {
                  setPaginationObj((prev) => ({
                    ...prev,
                    currentPage: prev.currentPage - 1,
                  }));
                }}
                onChangeLastPage={() => {
                  setPaginationObj((prev) => ({
                    ...prev,
                    currentPage: totalPages || 0,
                  }));
                }}
                pageSizeSelector={customPaginationPageSizeSelector}
                totalRecords={totalRecords}
              />
            </div>
          )}
        </div>
      </div>
      {showAdminSupportModal && (
        <AdminSupportModal
          show={showAdminSupportModal}
          onClose={handleCloseAdminSupportModal}
          isEdit={isEdit}
          adminData={adminSupportToEdit}
          setShowAdminSupportModal={setShowAdminSupportModal}
        />
      )}
      {deleteConfirmationModalVisible && (
        <ConfirmationModal
          isVisible={deleteConfirmationModalVisible}
          setIsVisible={setDeleteConfirmationVisibility}
          cancelButtonHandler={onCancelModalAction}
          confirmButtonHandler={handleDelete}
          header="Delete admin/support user?"
          message={
            <div>
              If you proceed, the user{" "}
              <b>{selectedRowForDeletion?.Name || "-"}</b> will be permanently
              deleted. This action cannot be undone.
            </div>
          }
          loading={isDeletingUser}
          error={deleteUserError || ""}
          cancelLabel="Cancel"
          confirmLabel={"Yes, delete"}
        />
      )}
      {toggleConfirmationModalVisible && (
        <ConfirmationModal
          isVisible={toggleConfirmationModalVisible}
          setIsVisible={setToggleConfirmationVisibility}
          cancelButtonHandler={onCancelToggleAction}
          confirmButtonHandler={confirmToggleActive}
          header={
            selectedRowForToggle?.IsActive
              ? "Deactivate this user?"
              : "Activate this user?"
          }
          message={
            <div>
              Are you sure you want to{" "}
              {selectedRowForToggle?.IsActive ? "deactivate" : "activate"}{" "}
              <b>
                <span className="italic-font">
                  {selectedRowForToggle?.Name || "-"}
                </span>
              </b>
              ?
              <br />
              {selectedRowForToggle?.IsActive &&
                `Note: They will no longer be able to access the system.`}
            </div>
          }
          loading={isTogglingActive}
          cancelLabel="Cancel"
          confirmLabel={
            selectedRowForToggle?.IsActive ? "Deactivate user" : "Activate user"
          }
        />
      )}
    </div>
  );
};

export default AdminSupport;
